import React from 'react';
import { Typography } from '@mui/material';

/**
 * RITA-1505 (Nutzungserfassung Phase 2c, Epic RITA-1496) — Bezeichner einer Ergebniszeile.
 *
 * Die Lese-API liefert drei Zeilenformen, und alle drei laufen durch diesen Baustein, damit sie
 * überall gleich aussehen und keine Tabelle sie einzeln erfindet:
 *
 * 1. `suppressed && users === 0` — es bleibt eine Zeile ohne Zahlen stehen. Sie unterscheidet
 *    „aus Datenschutzgründen nichts anzeigbar“ von „keine Nutzung“ (leere Liste).
 * 2. `suppressed` — Sammelzeile mehrerer zu kleiner Gruppen; `groups` sagt, wie viele.
 * 3. sichtbare Zeile — der Bezeichner, oder 'unbekannt', wenn er leer ist (eine leere Route
 *    entsteht bei 404 und Weiterleitungen).
 *
 * CONTRACT: Erkennungsmerkmal ist ausschließlich `suppressed`. Eine unterdrückte Zeile zeigt NIE
 * einen Bezeichner — auch dann nicht, wenn einer mitgeliefert würde: „diese Seite hat zu wenige
 * Nutzer“ wäre genau die Aussage, die die Unterdrückung verhindern soll (usage.graphql, Kopf).
 */
export interface SchutzZeileProps {
  suppressed: boolean;
  /** Zahl der zusammengefassten Gruppen (1 = echte Zeile). */
  groups: number;
  users: number;
  /** Route, Name oder Meldung der Zeile — an unterdrückten Zeilen null. */
  bezeichner: string | null | undefined;
  /** true = Bezeichner in Schreibmaschinenschrift (Routen, Schlüssel, Operationen). */
  technisch?: boolean;
}

export const SCHUTZ_OHNE_ZAHLEN = 'aus Datenschutzgründen nichts anzeigbar';
export const SCHUTZ_SAMMELZEILE = 'zu kleine Gruppen';
export const UNBEKANNT = 'unbekannt';

export default function SchutzZeile({ suppressed, groups, users, bezeichner, technisch = false }: SchutzZeileProps) {
  if (suppressed) {
    const text = users === 0 ? SCHUTZ_OHNE_ZAHLEN : `${SCHUTZ_SAMMELZEILE} (${groups})`;
    return (
      <Typography component="span" variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
        {text}
      </Typography>
    );
  }
  const text = bezeichner === null || bezeichner === undefined || bezeichner === '' ? UNBEKANNT : bezeichner;
  const unbekannt = text === UNBEKANNT;
  return (
    <Typography
      component="span"
      variant="body2"
      sx={{
        color: unbekannt ? 'text.secondary' : 'text.primary',
        fontFamily: technisch && !unbekannt ? 'Menlo, Consolas, monospace' : undefined,
        wordBreak: 'break-word',
      }}
    >
      {text}
    </Typography>
  );
}
